import { useState } from 'react';
import type { ReactElement } from 'react';
import { BookOpen, ChevronLeft, Share2, SlidersHorizontal } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router';
import type { Document, DocumentStatus, DocumentVersion } from '@xdocs/schemas';
import { Badge, Button, EmptyState, Skeleton, cn } from '@xdocs/ui';
import type { BadgeTone } from '@xdocs/ui';
import { useAuth } from '../../auth/AuthProvider.js';
import { useDocument } from '../documents/index.js';
import {
  AutosaveIndicator,
  ConflictBanner,
  EditorToolbar,
  ExportMenu,
  ManuscriptEditor,
  RestoreDraftPrompt,
  VersionHistorySidebar,
  useEditorAutosave,
} from '../../features/editor/index.js';
import { CutContinuePanel, DifficultyPanel, ExpandPanel, TranslatePanel } from '../../features/enhancement/index.js';
import { ShareDialog } from '../../features/sharing/index.js';
import { useCurrentVersion } from './useCurrentVersion.js';
import { useEditT } from './useEditT.js';
import styles from './EditorPage.module.css';

// mockup-20 (design/app/20-editor.html): document header status pill tone —
// same mapping `DocumentDetailPage` (`/documents/:id`) uses for its own
// status badge; duplicated here rather than imported since that route file
// is outside this task's ownership boundary.
const STATUS_TONE: Record<DocumentStatus, BadgeTone> = {
  draft: 'neutral',
  generating: 'info',
  ready: 'success',
  failed: 'error',
};

interface EditorWorkspaceProps {
  document: Document;
  version: DocumentVersion;
  isOwner: boolean;
}

/**
 * The loaded-state editor body (F67). Split from `EditorPage` so
 * `useEditorAutosave` only ever mounts with REAL initial data — the outer
 * page owns loading/error/not-found branching (never a conditional hook).
 * Keyed by `version.id` at the call site (below) so a genuinely new current
 * version (conflict reload, restore-as-new, another editor's save) remounts
 * this subtree with fresh state rather than needing bespoke re-seeding.
 */
function EditorWorkspace({ document, version, isOwner }: EditorWorkspaceProps): ReactElement {
  const { t } = useEditT();
  const autosave = useEditorAutosave(document.id, version.id, version.contentJsonb);
  const chapters = [...autosave.content.chapters].sort((a, b) => a.order - b.order);
  const [activeChapterId, setActiveChapterId] = useState<string | null>(chapters[0]?.id ?? null);
  const [restoreNonce, setRestoreNonce] = useState(0);
  const [showVersions, setShowVersions] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);

  const activeChapter = chapters.find((c) => c.id === activeChapterId) ?? chapters[0] ?? null;
  const readOnly = !isOwner;

  function updateChapter(chapterId: string, updates: { title?: string; body?: string }): void {
    const nextChapters = autosave.content.chapters.map((c) => (c.id === chapterId ? { ...c, ...updates } : c));
    autosave.setContent({ ...autosave.content, chapters: nextChapters });
  }

  function handleReload(): void {
    // Simplest safe fix for a live save conflict (F67 AC: "conflict banner
    // on CONFLICT w/ reload path") — a full reload re-fetches the document's
    // now-current version from scratch, never guessing at a merge.
    window.location.reload();
  }

  return (
    <div className={styles['page']}>
      <div className={styles['tree']}>
        <Link to="/documents" className={styles['treeBreadcrumb']}>
          <ChevronLeft size={14} aria-hidden="true" />
          {t('edit:chapters.breadcrumb')}
        </Link>
        <p className={styles['treeTitle']}>{t('edit:chapters.title', { count: chapters.length })}</p>
        {chapters.length === 0 ? (
          <p>{t('edit:chapters.empty')}</p>
        ) : (
          <ul className={styles['chapterList']}>
            {chapters.map((chapter, index) => (
              <li key={chapter.id}>
                <button
                  type="button"
                  className={styles['chapterButton']}
                  data-active={chapter.id === activeChapter?.id}
                  onClick={() => setActiveChapterId(chapter.id)}
                >
                  {/* mockup-20: chapter chips are always zero-padded to 2
                      digits ("01".."08"), independent of the total count. */}
                  <span className={styles['chapterOrder']}>{String(index + 1).padStart(2, '0')}</span>
                  <span>{chapter.title}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className={styles['content']}>
        <div className={styles['docHeader']}>
          <div className={styles['docHeaderTitleRow']}>
            <h1 className={styles['docTitle']}>{document.title}</h1>
            <Badge tone={STATUS_TONE[document.status]}>{t(`edit:status.${document.status}`)}</Badge>
          </div>
          <div className={styles['docHeaderMeta']}>
            <span className={styles['docLanguage']}>{document.language.toUpperCase()}</span>
            {isOwner && (
              <Button
                variant="primary"
                size="sm"
                leadingIcon={<Share2 size={16} aria-hidden="true" />}
                onClick={() => setShareDialogOpen(true)}
              >
                {t('edit:action.share')}
              </Button>
            )}
          </div>
        </div>

        <div className={styles['contentHeader']}>
          <EditorToolbar disabled={readOnly} className={styles['headerToolbar']} />
          <AutosaveIndicator status={autosave.status} lastSavedAt={autosave.lastSavedAt} onRetry={autosave.saveNow} />
        </div>

        {autosave.status === 'conflict' && (
          <div className={styles['conflictWrap']}>
            <ConflictBanner documentTitle={document.title} onReload={handleReload} />
          </div>
        )}

        <div className={styles['editorScroll']}>
          {activeChapter ? (
            <ManuscriptEditor
              title={activeChapter.title}
              body={activeChapter.body}
              onTitleChange={(title) => updateChapter(activeChapter.id, { title })}
              onBodyChange={(body) => updateChapter(activeChapter.id, { body })}
              resetKey={`${activeChapter.id}:${restoreNonce}`}
              readOnly={readOnly}
            />
          ) : (
            <EmptyState kind="first-use" title={t('edit:chapters.empty')} mascotAlt="" />
          )}
        </div>

        {isOwner && (
          <ShareDialog
            open={shareDialogOpen}
            onOpenChange={setShareDialogOpen}
            documentId={document.id}
            documentTitle={document.title}
          />
        )}
      </div>

      <div className={styles['rail']}>
        <div className={styles['railTopActions']}>
          <Link to={`/read/${document.id}`} className={styles['railActionButton']}>
            <BookOpen size={16} aria-hidden="true" />
            {t('edit:action.read')}
          </Link>
          <ExportMenu
            documentId={document.id}
            documentTitle={document.title}
            versionId={version.id}
            className={styles['railExportButton']}
          />
        </div>

        {isOwner && (
          <>
            <p className={styles['railSection']}>{t('edit:enhance.title')}</p>
            <div className={styles['enhanceList']}>
              <ExpandPanel documentId={document.id} documentTitle={document.title} versionId={version.id} />
              <CutContinuePanel
                documentId={document.id}
                documentTitle={document.title}
                versionId={version.id}
                chapters={chapters.map((c) => ({ id: c.id, title: c.title }))}
              />
              <DifficultyPanel
                documentId={document.id}
                documentTitle={document.title}
                versionId={version.id}
                currentLevel={document.difficultyLevel}
              />
              <TranslatePanel documentId={document.id} documentTitle={document.title} versionId={version.id} />
              {/* mockup-20: "Themes & styling" rail entry — WIRING.md open
                  issue #6, now that `/style/:docId` (F65) exists. Non-PRO,
                  a real link (not a modal trigger like the four panels
                  above), so it's a plain <Link> styled to match `ListRow`'s
                  visual rhythm rather than reusing that primitive's
                  onClick-only (button) API. */}
              <Link to={`/style/${document.id}`} className={styles['enhanceStyleLink']}>
                <span className={styles['enhanceStyleIcon']} aria-hidden="true">
                  <SlidersHorizontal size={16} />
                </span>
                <span className={styles['enhanceStyleTitle']}>{t('edit:action.style')}</span>
              </Link>
            </div>
          </>
        )}

        <p className={styles['railSection']}>{t('edit:versions.toggle')}</p>
        <Button variant="secondary" size="sm" className={styles['versionsToggle']} onClick={() => setShowVersions((v) => !v)}>
          {t('edit:versions.toggle')}
        </Button>
        {showVersions && (
          <VersionHistorySidebar
            documentId={document.id}
            documentTitle={document.title}
            currentVersionId={version.id}
            onRestored={() => setShowVersions(false)}
          />
        )}
      </div>

      <RestoreDraftPrompt
        open={autosave.restoreAvailable !== null}
        documentTitle={document.title}
        onRestore={() => {
          autosave.restoreDraft();
          setRestoreNonce((n) => n + 1);
        }}
        onDiscard={autosave.discardDraft}
      />
    </div>
  );
}

/**
 * `/edit/:docId` (F67 — WYSIWYG editor + autosave + export + version
 * history). ACL note (open issue): the owner/`share_role='editor'`
 * distinction (§15.3) needs a `share.get`-shaped read this build doesn't
 * have client-side yet (WS-SHARE ownership) — this page falls back to
 * strict `ownerId === me` for the WYSIWYG write surface, degrading a
 * genuine `editor` grantee to read-only until that ACL signal lands
 * client-side. The server remains the fail-closed authority regardless
 * (§15) — a mis-gated client attempt still gets `FORBIDDEN`, never silently
 * allowed.
 */
export function EditorPage(): ReactElement {
  const { docId } = useParams<{ docId: string }>();
  const navigate = useNavigate();
  const { t } = useEditT();
  const { userId } = useAuth();
  const documentId = docId ?? '';

  const { document, isLoading: isDocLoading, isError: isDocError, error: docError, refetch: refetchDoc } = useDocument(documentId);
  const { version, isLoading: isVersionLoading, isError: isVersionError, refetch: refetchVersion } = useCurrentVersion(
    documentId,
    document?.currentVersionId ?? null,
  );

  if (!docId) {
    return <EmptyState kind="error" title={t('edit:notFound.title')} />;
  }

  const isLoading = isDocLoading || (document !== undefined && document.currentVersionId !== null && isVersionLoading);

  if (isLoading) {
    return (
      <div className={cn(styles['loadingPage'])} aria-busy="true">
        <Skeleton shape="card" count={3} />
      </div>
    );
  }

  if (isDocError) {
    if (docError?.code === 'NOT_FOUND') {
      return <EmptyState kind="error" title={t('edit:notFound.title')} action={{ label: t('edit:notFound.cta'), onClick: () => void navigate('/documents') }} />;
    }
    return <EmptyState kind="error" title={t('edit:error.title')} action={{ label: t('edit:error.retry'), onClick: refetchDoc }} />;
  }

  if (!document) {
    return <EmptyState kind="error" title={t('edit:notFound.title')} />;
  }

  if (document.currentVersionId === null) {
    return <EmptyState kind="first-use" title={t('edit:chapters.empty')} mascotAlt="" />;
  }

  if (isVersionError || !version) {
    return <EmptyState kind="error" title={t('edit:error.title')} action={{ label: t('edit:error.retry'), onClick: refetchVersion }} />;
  }

  const isOwner = userId !== null && document.ownerId === userId;

  return <EditorWorkspace key={version.id} document={document} version={version} isOwner={isOwner} />;
}
